import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Brain, Sparkle, MagicWand, FloppyDisk, Copy, Trophy, Archive,
  FunnelSimple, ArrowClockwise, FileText, Lightning, Tag as TagIcon,
  ClipboardText, Quotes, SealCheck, Megaphone, ChartLineUp, Star,
} from "@phosphor-icons/react";
import { PageHeader, StatCard, SectionCard, EmptyState } from "../components/ui/kaizen";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { CampaignSelect } from "../components/CampaignSelect";

const TABS = [
  { id: "dashboard", label: "Tableau de bord", icon: ChartLineUp },
  { id: "generator", label: "Générateur", icon: MagicWand },
  { id: "contents", label: "Contenus", icon: FileText },
  { id: "prompts", label: "Prompts", icon: ClipboardText },
  { id: "winners", label: "Gagnants", icon: Trophy },
];

const TEMPLATE_ICONS = { objection: Quotes, transcript: FileText, proof: SealCheck, offer: Megaphone };

const STATUS_TONE = {
  draft: "bg-zinc-700/40 text-zinc-300",
  validated: "bg-sky-500/15 text-sky-300",
  testing: "bg-amber-500/15 text-amber-300",
  published: "bg-emerald-500/15 text-emerald-300",
  winner: "bg-yellow-500/20 text-yellow-300",
  archived: "bg-zinc-800 text-zinc-500",
  active: "bg-emerald-500/15 text-emerald-300",
};

const inputCls =
  "w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-100 focus:border-amber-500/50 focus:outline-none";

function StatusBadge({ status }) {
  return (
    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-md ${STATUS_TONE[status] || STATUS_TONE.draft}`}>
      {status}
    </span>
  );
}

function CopyBtn({ text, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={() => { navigator.clipboard.writeText(text || ""); toast.success("Copié"); }}
      className="text-zinc-500 hover:text-amber-400 transition-colors shrink-0"
      title="Copier"
    >
      <Copy size={15} />
    </button>
  );
}

// ─── Rendu de la sortie IA (5 sections) ───────────────────────────────────────
function OutputView({ output }) {
  if (!output) return null;
  const safe = (v) => {
    if (v == null) return "";
    if (typeof v === "string") return v;
    if (Array.isArray(v)) return v.map(safe).join("\n");
    if (typeof v === "object") return Object.values(v).map(safe).join(" · ");
    return String(v);
  };
  const sd = output.strategic_diagnosis || {};
  const ma = output.main_angle || {};
  const mo = output.marketing_outputs || {};
  const va = output.variants || {};
  const an = output.application_notes || {};

  const Field = ({ label, value }) => {
    const v = safe(value);
    return v ? (
      <div className="flex items-start justify-between gap-2 py-1.5 border-b border-zinc-800/50 last:border-0">
        <span className="text-xs text-zinc-500 font-manrope shrink-0 w-40">{label}</span>
        <span className="text-sm text-zinc-200 flex-1">{v}</span>
        <CopyBtn text={v} testid={`copy-${label}`} />
      </div>
    ) : null;
  };

  const TextBlock = ({ label, value }) => {
    const v = safe(value);
    return v ? (
      <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4" data-testid={`output-${label}`}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-bold uppercase tracking-wide text-amber-400">{label}</span>
          <CopyBtn text={v} testid={`copy-block-${label}`} />
        </div>
        <p className="text-sm text-zinc-200 whitespace-pre-wrap leading-relaxed">{v}</p>
      </div>
    ) : null;
  };

  const List = ({ label, items }) => {
    const arr = Array.isArray(items) ? items.map(safe).filter(Boolean) : [];
    return arr.length ? (
      <div>
        <span className="text-xs font-bold uppercase tracking-wide text-zinc-400">{label}</span>
        <ul className="mt-1.5 space-y-1.5">
          {arr.map((it, i) => (
            <li key={i} className="flex items-start justify-between gap-2 text-sm text-zinc-200 bg-zinc-950 border border-zinc-800 rounded-md px-3 py-1.5">
              <span className="flex-1">{it}</span>
              <CopyBtn text={it} testid={`copy-variant-${label}-${i}`} />
            </li>
          ))}
        </ul>
      </div>
    ) : null;
  };

  return (
    <div className="space-y-5" data-testid="ai-output-view">
      <SectionCard title="A · Diagnostic stratégique" icon={Brain}>
        <div className="-my-3">
          <Field label="Type de contenu" value={sd.content_type} />
          <Field label="Tension principale" value={sd.main_tension} />
          <Field label="Croyance actuelle" value={sd.current_belief} />
          <Field label="Croyance cible" value={sd.target_belief} />
          <Field label="Shift psychologique" value={sd.psychological_shift} />
          <Field label="Objectif business" value={sd.business_goal} />
        </div>
      </SectionCard>

      <SectionCard title="B · Angle principal" icon={Lightning}>
        <div className="-my-3">
          <Field label="Idée centrale" value={ma.core_idea} />
          <Field label="Reframing" value={ma.reframing} />
          <Field label="Promesse implicite" value={ma.implicit_promise} />
          <Field label="Coût de l'inaction" value={ma.cost_of_inaction} />
        </div>
      </SectionCard>

      <SectionCard title="C · Outputs marketing" icon={Megaphone}>
        <div className="grid gap-3">
          <TextBlock label="Hook principal" value={mo.main_hook} />
          <TextBlock label="Version courte" value={mo.short_version} />
          <TextBlock label="Script Reel" value={mo.reel_script} />
          <TextBlock label="Caption" value={mo.caption} />
          <TextBlock label="Email court" value={mo.short_email} />
          <TextBlock label="Version FAQ" value={mo.faq_version} />
          <TextBlock label="Version page de vente" value={mo.sales_page_version} />
        </div>
      </SectionCard>

      <SectionCard title="D · Variantes" icon={Sparkle}>
        <div className="grid md:grid-cols-3 gap-5">
          <List label="Hooks alternatifs" items={va.alternate_hooks} />
          <List label="CTA alternatifs" items={va.alternate_ctas} />
          <List label="Angles alternatifs" items={va.alternate_angles} />
        </div>
      </SectionCard>

      <SectionCard title="E · Notes d'application" icon={SealCheck}>
        <div className="-my-3">
          <Field label="Meilleur cas d'usage" value={an.best_use_case} />
          <Field label="Niveau de conscience idéal" value={an.ideal_awareness_level} />
          <Field label="Étape funnel idéale" value={an.best_funnel_stage} />
          <Field label="Canal recommandé" value={an.recommended_channel} />
          <Field label="Métrique clé à suivre" value={an.key_metric_to_watch} />
        </div>
      </SectionCard>
    </div>
  );
}

// ─── Onglet Tableau de bord ───────────────────────────────────────────────────
function DashboardTab({ onNew }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/marketing-ai/dashboard").then((r) => setData(r.data)).catch(() => toast.error("Erreur de chargement"));
  }, []);
  if (!data) return <div className="text-zinc-500 text-sm">Chargement…</div>;
  const s = data.stats;
  return (
    <div className="space-y-6" data-testid="mkai-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard label="Contenus" value={s.total_contents} icon={FileText} />
        <StatCard label="Sauvegardés" value={s.saved} icon={FloppyDisk} />
        <StatCard label="Gagnants" value={s.winners} icon={Trophy} />
        <StatCard label="Prompts" value={s.templates} icon={ClipboardText} />
        <StatCard label="Requêtes" value={s.requests} icon={MagicWand} />
      </div>
      <SectionCard
        title="Contenus récents"
        icon={Lightning}
        headerActions={<Button size="sm" data-testid="dashboard-new-btn" onClick={onNew} className="bg-amber-500 text-zinc-950 hover:bg-amber-400"><MagicWand size={15} className="mr-1.5" />Nouveau contenu</Button>}
      >
        {data.recent?.length ? (
          <div className="grid md:grid-cols-2 gap-3">
            {data.recent.map((c) => (
              <div key={c.id} className="bg-zinc-950 border border-zinc-800 rounded-lg p-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm text-zinc-200 truncate">{c.title}</p>
                  <p className="text-xs text-zinc-500 truncate">{c.channel || "—"}</p>
                </div>
                <StatusBadge status={c.status} />
              </div>
            ))}
          </div>
        ) : (
          <EmptyState icon={Brain} title="Aucun contenu encore" description="Génère ton premier actif marketing avec l'IA." actionLabel="Nouveau contenu" onAction={onNew} />
        )}
      </SectionCard>
    </div>
  );
}

// ─── Onglet Générateur ────────────────────────────────────────────────────────
const EMPTY_FORM = {
  offer_name: "", offer_category: "", offer_description: "", target_audience: "",
  awareness_level: "", funnel_stage: "", input_type: "", source_text: "",
  main_objection: "", main_problem: "", current_belief: "", target_belief: "",
  core_promise: "", concrete_result: "", proof_available: "", differentiator: "",
  cost_of_inaction: "", desired_cta: "", channel: "", requested_format: "",
  tone: "", length: "", variants_count: 3, campaign_name: "", language: "fr",
  campaign_id: null,
};

function GeneratorTab({ prefill }) {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveMeta, setSaveMeta] = useState({ title: "", tags: "", channel: "", objection: "" });

  useEffect(() => {
    api.get("/marketing-ai/prompts?status=active").then((r) => {
      setTemplates(r.data.templates);
      if (r.data.templates[0]) setTemplateId(r.data.templates[0].id);
    });
  }, []);

  useEffect(() => {
    if (prefill && prefill.form) {
      setForm((f) => ({ ...f, ...prefill.form }));
      if (prefill.template_id) setTemplateId(prefill.template_id);
      setResult(null);
    }
  }, [prefill]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const generate = async () => {
    if (!templateId) return toast.error("Choisis un template");
    if (!form.source_text.trim()) return toast.error("Le texte source est requis");
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/marketing-ai/generate", { ...form, template_id: templateId, variants_count: Number(form.variants_count) || 3 });
      setResult(data);
      if (!data.parse_ok) toast.warning("Sortie IA non structurée — texte brut affiché");
      else toast.success("Contenu généré");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de génération");
    } finally {
      setLoading(false);
    }
  };

  const openSave = () => {
    const hook = result?.output?.marketing_outputs?.main_hook || "";
    setSaveMeta({ title: hook.slice(0, 100), tags: "", channel: form.channel, objection: form.main_objection });
    setSaveOpen(true);
  };

  const doSave = async () => {
    try {
      await api.post("/marketing-ai/contents", {
        request_id: result.request_id,
        output: result.output || {},
        raw: result.raw || "",
        template_id: templateId,
        campaign_name: form.campaign_name,
        campaign_id: form.campaign_id,
        channel: saveMeta.channel,
        objection: saveMeta.objection,
        title: saveMeta.title,
        tags: saveMeta.tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Contenu sauvegardé dans la bibliothèque");
      setSaveOpen(false);
    } catch {
      toast.error("Erreur de sauvegarde");
    }
  };

  const Group = ({ title, children }) => (
    <div className="space-y-3">
      <p className="text-xs font-bold uppercase tracking-wide text-zinc-500">{title}</p>
      <div className="grid md:grid-cols-2 gap-3">{children}</div>
    </div>
  );
  const F = ({ k, label, ph, area }) => (
    <div className={area ? "md:col-span-2" : ""}>
      <Label className="text-xs text-zinc-400 mb-1 block">{label}</Label>
      {area ? (
        <Textarea data-testid={`form-${k}`} value={form[k]} onChange={set(k)} placeholder={ph} className="bg-zinc-950 border-zinc-800 text-zinc-100 min-h-[80px]" />
      ) : (
        <input data-testid={`form-${k}`} value={form[k]} onChange={set(k)} placeholder={ph} className={inputCls} />
      )}
    </div>
  );

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="mkai-generator">
      {/* Formulaire */}
      <div className="space-y-5">
        <SectionCard title="1 · Template de prompt" icon={ClipboardText}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {templates.map((t) => {
              const Ic = TEMPLATE_ICONS[t.slug] || ClipboardText;
              const active = t.id === templateId;
              return (
                <button
                  key={t.id}
                  data-testid={`template-pick-${t.slug}`}
                  onClick={() => setTemplateId(t.id)}
                  className={`p-3 rounded-lg border text-left transition-all ${active ? "border-amber-500 bg-amber-500/10" : "border-zinc-800 bg-zinc-950 hover:border-zinc-700"}`}
                >
                  <Ic size={20} weight="duotone" className={active ? "text-amber-400" : "text-zinc-500"} />
                  <p className="text-xs font-semibold text-zinc-200 mt-1.5">{t.name}</p>
                </button>
              );
            })}
          </div>
        </SectionCard>

        <SectionCard title="2 · Contexte structuré" icon={FileText}>
          <Group title="Offre & marché">
            <F k="offer_name" label="Nom de l'offre" ph="Cours d'essai (19,98 $)" />
            <F k="offer_category" label="Catégorie" ph="Muay Thaï" />
            <F k="offer_description" label="Description courte" ph="…" area />
            <F k="target_audience" label="Audience cible" ph="Adultes débutants Val-d'Or" />
            <F k="awareness_level" label="Niveau de conscience" ph="Problème conscient" />
            <F k="funnel_stage" label="Étape funnel" ph="TOFU / MOFU / BOFU" />
          </Group>
          <Group title="Input">
            <F k="input_type" label="Type d'input" ph="objection / transcript / idée…" />
            <F k="source_text" label="Texte source *" ph="Colle l'objection, le transcript, l'idée…" area />
            <F k="main_objection" label="Objection principale" ph="Je n'ai pas le temps" />
            <F k="main_problem" label="Problème principal" ph="…" />
            <F k="current_belief" label="Croyance actuelle" ph="…" />
            <F k="target_belief" label="Croyance cible" ph="…" />
          </Group>
          <Group title="Logique marketing">
            <F k="core_promise" label="Promesse centrale" ph="…" />
            <F k="concrete_result" label="Résultat concret" ph="…" />
            <F k="proof_available" label="Preuve disponible" ph="Témoignage, chiffre…" />
            <F k="differentiator" label="Différenciateur" ph="…" />
            <F k="cost_of_inaction" label="Coût de l'inaction" ph="…" />
            <F k="desired_cta" label="CTA désiré" ph="Réserve ton essai" />
          </Group>
          <Group title="Exécution">
            <F k="channel" label="Canal" ph="Instagram" />
            <F k="requested_format" label="Format demandé" ph="Reel / Carrousel / Email" />
            <F k="tone" label="Ton" ph="Direct et rassurant" />
            <F k="length" label="Longueur" ph="Court / Moyen / Long" />
            <div>
              <Label className="text-xs text-zinc-400 mb-1 block">Nombre de variantes</Label>
              <input type="number" min="1" max="8" data-testid="form-variants_count" value={form.variants_count} onChange={set("variants_count")} className={inputCls} />
            </div>
            <div>
              <Label className="text-xs text-zinc-400 mb-1 block">Langue</Label>
              <select data-testid="form-language" value={form.language} onChange={set("language")} className={inputCls}>
                <option value="fr">Français</option>
                <option value="en">English</option>
              </select>
            </div>
            <F k="campaign_name" label="Campagne (libellé libre)" ph="Reconquête été 2026" />
            <div className="md:col-span-2">
              <CampaignSelect
                value={form.campaign_id}
                onChange={(v) => setForm((f) => ({ ...f, campaign_id: v }))}
                label="Rattacher à une campagne (Planificateur)"
                labelClassName="text-xs text-zinc-400 mb-1 block"
                className={inputCls}
                testid="mkai-campaign-select"
              />
            </div>
          </Group>
          <Button data-testid="generate-btn" onClick={generate} disabled={loading} className="w-full bg-amber-500 text-zinc-950 hover:bg-amber-400 font-bold">
            {loading ? <><ArrowClockwise size={16} className="mr-2 animate-spin" />Génération…</> : <><MagicWand size={16} className="mr-2" />Générer avec l'IA</>}
          </Button>
        </SectionCard>
      </div>

      {/* Résultats */}
      <div className="space-y-4">
        {loading && (
          <div className="flex flex-col items-center justify-center min-h-[400px] text-zinc-500 gap-3">
            <ArrowClockwise size={32} className="animate-spin text-amber-500" />
            <p className="text-sm">Claude Sonnet 4.6 rédige tes contenus…</p>
          </div>
        )}
        {!loading && !result && (
          <EmptyState icon={MagicWand} title="Résultats de génération" description="Remplis le formulaire et lance la génération. Les outputs apparaîtront ici." />
        )}
        {!loading && result && (
          <>
            <div className="flex items-center justify-between sticky top-0 z-10 bg-zinc-950/80 backdrop-blur py-2">
              <p className="text-sm font-semibold text-zinc-200">Résultats</p>
              <Button data-testid="save-content-btn" onClick={openSave} disabled={!result.output} className="bg-emerald-600 hover:bg-emerald-500 text-white">
                <FloppyDisk size={15} className="mr-1.5" />Sauvegarder
              </Button>
            </div>
            {result.parse_ok ? (
              <OutputView output={result.output} />
            ) : (
              <SectionCard title="Sortie brute (non structurée)" icon={FileText}>
                <pre className="text-xs text-zinc-300 whitespace-pre-wrap">{result.raw}</pre>
              </SectionCard>
            )}
          </>
        )}
      </div>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
          <DialogHeader><DialogTitle>Sauvegarder le contenu</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-zinc-400 mb-1 block">Titre</Label>
              <input data-testid="save-title" value={saveMeta.title} onChange={(e) => setSaveMeta((m) => ({ ...m, title: e.target.value }))} className={inputCls} /></div>
            <div><Label className="text-xs text-zinc-400 mb-1 block">Canal</Label>
              <input data-testid="save-channel" value={saveMeta.channel} onChange={(e) => setSaveMeta((m) => ({ ...m, channel: e.target.value }))} className={inputCls} /></div>
            <div><Label className="text-xs text-zinc-400 mb-1 block">Tags (séparés par virgule)</Label>
              <input data-testid="save-tags" value={saveMeta.tags} onChange={(e) => setSaveMeta((m) => ({ ...m, tags: e.target.value }))} className={inputCls} /></div>
            <Button data-testid="confirm-save-btn" onClick={doSave} className="w-full bg-emerald-600 hover:bg-emerald-500 text-white">Sauvegarder</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Détail contenu (dialog) ──────────────────────────────────────────────────
function buildReusePrefill(content) {
  const o = content.output || {};
  const ma = o.main_angle || {};
  const sd = o.strategic_diagnosis || {};
  const parts = [];
  if (ma.core_idea) parts.push(`Angle gagnant à réutiliser : ${ma.core_idea}`);
  if (ma.reframing) parts.push(`Reframing : ${ma.reframing}`);
  if (ma.implicit_promise) parts.push(`Promesse : ${ma.implicit_promise}`);
  return {
    template_id: content.template_id,
    form: {
      offer_name: content.title || "",
      channel: content.channel || "",
      main_objection: content.objection || "",
      current_belief: sd.current_belief || "",
      target_belief: sd.target_belief || "",
      campaign_name: content.campaign_name || "",
      source_text: parts.join("\n") || (ma.core_idea || ""),
    },
  };
}

function ContentDetail({ id, onClose, onChanged, onReuse }) {
  const [data, setData] = useState(null);
  const [metrics, setMetrics] = useState({ impressions: "", clicks: "", leads: "", sales: "" });
  const load = useCallback(() => {
    if (id) api.get(`/marketing-ai/contents/${id}`).then((r) => setData(r.data));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const act = async (path, body) => {
    try { await api.post(`/marketing-ai/contents/${id}/${path}`, body || {}); toast.success("Mis à jour"); load(); onChanged?.(); }
    catch { toast.error("Erreur"); }
  };
  const changeStatus = async (status) => {
    try { await api.patch(`/marketing-ai/contents/${id}/status`, { status }); toast.success("Statut mis à jour"); load(); onChanged?.(); }
    catch { toast.error("Erreur"); }
  };
  const saveMetrics = async () => {
    await api.post(`/marketing-ai/contents/${id}/metrics`, {
      impressions: Number(metrics.impressions) || 0, clicks: Number(metrics.clicks) || 0,
      leads: Number(metrics.leads) || 0, sales: Number(metrics.sales) || 0,
    });
    toast.success("Métriques enregistrées"); load();
  };

  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-3xl max-h-[88vh] overflow-y-auto">
        {data && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 flex-wrap">
                <span className="truncate">{data.content.title}</span>
                <StatusBadge status={data.content.status} />
                {data.content.is_winner && <Trophy size={16} className="text-yellow-400" weight="fill" />}
              </DialogTitle>
            </DialogHeader>
            <div className="flex flex-wrap gap-2 mb-4">
              <Button size="sm" data-testid="detail-winner" onClick={() => act("winner")} className="bg-yellow-600 hover:bg-yellow-500 text-white"><Trophy size={14} className="mr-1" />Marquer gagnant</Button>
              <Button size="sm" data-testid="detail-archive" variant="outline" onClick={() => act("archive")} className="border-zinc-700"><Archive size={14} className="mr-1" />Archiver</Button>
              {onReuse && (
                <Button size="sm" data-testid="detail-reuse" onClick={() => { onReuse(buildReusePrefill(data.content)); onClose(); }} className="bg-amber-500 text-zinc-950 hover:bg-amber-400"><ArrowClockwise size={14} className="mr-1" />Réutiliser comme source</Button>
              )}
              {["draft", "validated", "testing", "published"].map((st) => (
                <Button key={st} size="sm" variant="outline" data-testid={`detail-status-${st}`} onClick={() => changeStatus(st)} className="border-zinc-700 text-xs">{st}</Button>
              ))}
            </div>
            <SectionCard title="Performance" icon={ChartLineUp}>
              <div className="grid grid-cols-4 gap-2">
                {["impressions", "clicks", "leads", "sales"].map((m) => (
                  <div key={m}>
                    <Label className="text-[11px] text-zinc-400">{m}</Label>
                    <input data-testid={`metric-${m}`} value={metrics[m]} onChange={(e) => setMetrics((x) => ({ ...x, [m]: e.target.value }))} className={inputCls} />
                  </div>
                ))}
              </div>
              <Button size="sm" data-testid="save-metrics" onClick={saveMetrics} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 w-fit">Enregistrer métriques</Button>
              {data.metrics?.length > 0 && (
                <p className="text-xs text-zinc-500">{data.metrics.length} relevé(s) enregistré(s)</p>
              )}
            </SectionCard>
            <div className="mt-4">
              <OutputView output={data.content.output} />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Onglet Contenus ──────────────────────────────────────────────────────────
function ContentsTab({ onReuse }) {
  const [contents, setContents] = useState([]);
  const [filters, setFilters] = useState({ search: "", channel: "", status: "" });
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (filters.search) p.set("search", filters.search);
    if (filters.channel) p.set("channel", filters.channel);
    if (filters.status) p.set("status", filters.status);
    api.get(`/marketing-ai/contents?${p.toString()}`).then((r) => setContents(r.data.contents));
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="mkai-contents">
      <SectionCard title="Filtres" icon={FunnelSimple}>
        <div className="grid md:grid-cols-3 gap-3">
          <input data-testid="filter-search" placeholder="Rechercher…" value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} className={inputCls} />
          <input data-testid="filter-channel" placeholder="Canal" value={filters.channel} onChange={(e) => setFilters((f) => ({ ...f, channel: e.target.value }))} className={inputCls} />
          <select data-testid="filter-status" value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} className={inputCls}>
            <option value="">Tous les statuts</option>
            {["draft", "validated", "testing", "published", "winner", "archived"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </SectionCard>
      {contents.length ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {contents.map((c) => (
            <button key={c.id} data-testid={`content-card-${c.id}`} onClick={() => setDetailId(c.id)} className="text-left bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-amber-500/40 transition-all">
              <div className="flex items-start justify-between gap-2 mb-2">
                <StatusBadge status={c.status} />
                {c.is_winner && <Trophy size={15} className="text-yellow-400" weight="fill" />}
              </div>
              <p className="text-sm text-zinc-100 font-medium line-clamp-2">{c.title}</p>
              <p className="text-xs text-zinc-500 mt-2">{c.channel || "—"} · {c.campaign_name || "Sans campagne"}</p>
              {c.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {c.tags.map((t) => <Badge key={t} variant="outline" className="text-[10px] border-zinc-700 text-zinc-400">{t}</Badge>)}
                </div>
              )}
            </button>
          ))}
        </div>
      ) : (
        <EmptyState icon={FileText} title="Aucun contenu sauvegardé" description="Génère et sauvegarde des contenus pour les retrouver ici." />
      )}
      <ContentDetail id={detailId} onClose={() => setDetailId(null)} onChanged={load} onReuse={onReuse} />
    </div>
  );
}

// ─── Détail prompt (dialog) ───────────────────────────────────────────────────
function PromptDetail({ id, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [editText, setEditText] = useState("");
  const load = useCallback(() => {
    if (id) api.get(`/marketing-ai/prompts/${id}`).then((r) => { setData(r.data); setEditText(r.data.template.prompt_text); });
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const duplicate = async () => { await api.post(`/marketing-ai/prompts/${id}/duplicate`); toast.success("Dupliqué"); onChanged?.(); onClose(); };
  const newVersion = async () => { await api.post(`/marketing-ai/prompts/${id}/versions`, { content: editText }); toast.success("Nouvelle version"); load(); onChanged?.(); };
  const setStatus = async (status) => { await api.patch(`/marketing-ai/prompts/${id}/status`, { status }); toast.success("Statut mis à jour"); load(); onChanged?.(); };

  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-2xl">
        {data && (
          <>
            <DialogHeader><DialogTitle className="flex items-center gap-2">{data.template.name}<StatusBadge status={data.template.status} /><span className="text-xs text-zinc-500">v{data.template.version}</span></DialogTitle></DialogHeader>
            <p className="text-sm text-zinc-400">{data.template.description}</p>
            <Textarea data-testid="prompt-edit-text" value={editText} onChange={(e) => setEditText(e.target.value)} className="bg-zinc-950 border-zinc-800 text-zinc-100 min-h-[160px]" />
            <div className="flex flex-wrap gap-2">
              <Button size="sm" data-testid="prompt-new-version" onClick={newVersion} className="bg-amber-500 text-zinc-950 hover:bg-amber-400">Créer une version</Button>
              <Button size="sm" data-testid="prompt-duplicate" variant="outline" onClick={duplicate} className="border-zinc-700"><Copy size={14} className="mr-1" />Dupliquer</Button>
              {["active", "testing", "draft", "archived"].map((s) => (
                <Button key={s} size="sm" variant="outline" data-testid={`prompt-status-${s}`} onClick={() => setStatus(s)} className="border-zinc-700 text-xs">{s}</Button>
              ))}
            </div>
            {data.versions?.length > 0 && <p className="text-xs text-zinc-500">{data.versions.length} version(s) archivée(s)</p>}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Onglet Prompts ───────────────────────────────────────────────────────────
function PromptsTab() {
  const [templates, setTemplates] = useState([]);
  const [search, setSearch] = useState("");
  const [detailId, setDetailId] = useState(null);
  const load = useCallback(() => {
    const p = search ? `?search=${encodeURIComponent(search)}` : "";
    api.get(`/marketing-ai/prompts${p}`).then((r) => setTemplates(r.data.templates));
  }, [search]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="mkai-prompts">
      <input data-testid="prompt-search" placeholder="Rechercher un prompt…" value={search} onChange={(e) => setSearch(e.target.value)} className={`${inputCls} max-w-md`} />
      <div className="grid md:grid-cols-2 gap-3">
        {templates.map((t) => {
          const Ic = TEMPLATE_ICONS[t.slug] || ClipboardText;
          return (
            <button key={t.id} data-testid={`prompt-card-${t.id}`} onClick={() => setDetailId(t.id)} className="text-left bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-amber-500/40 transition-all">
              <div className="flex items-center justify-between mb-2">
                <Ic size={22} weight="duotone" className="text-amber-400" />
                <div className="flex items-center gap-2"><StatusBadge status={t.status} /><span className="text-xs text-zinc-500">v{t.version}</span></div>
              </div>
              <p className="text-sm font-semibold text-zinc-100">{t.name}</p>
              <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{t.description}</p>
            </button>
          );
        })}
      </div>
      <PromptDetail id={detailId} onClose={() => setDetailId(null)} onChanged={load} />
    </div>
  );
}

// ─── Onglet Gagnants ──────────────────────────────────────────────────────────
function WinnersTab({ onReuse }) {
  const [contents, setContents] = useState([]);
  const [detailId, setDetailId] = useState(null);
  const load = useCallback(() => { api.get("/marketing-ai/contents?winner=true").then((r) => setContents(r.data.contents)); }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="mkai-winners">
      {contents.length ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {contents.map((c) => (
            <button key={c.id} data-testid={`winner-card-${c.id}`} onClick={() => setDetailId(c.id)} className="text-left bg-zinc-900 border border-yellow-500/30 rounded-xl p-4 hover:border-yellow-500/60 transition-all">
              <Trophy size={20} className="text-yellow-400 mb-2" weight="fill" />
              <p className="text-sm text-zinc-100 font-medium line-clamp-2">{c.title}</p>
              <p className="text-xs text-zinc-500 mt-2">{c.channel || "—"}</p>
            </button>
          ))}
        </div>
      ) : (
        <EmptyState icon={Trophy} title="Aucun asset gagnant" description="Marque tes meilleurs contenus comme gagnants pour les retrouver ici." />
      )}
      <ContentDetail id={detailId} onClose={() => setDetailId(null)} onChanged={load} onReuse={onReuse} />
    </div>
  );
}

// ─── Module ───────────────────────────────────────────────────────────────────
export default function MarketingAIModule() {
  const [tab, setTab] = useState("dashboard");
  const [prefill, setPrefill] = useState(null);
  const reuse = (p) => { setPrefill({ ...p, _ts: Date.now() }); setTab("generator"); };
  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="marketing-ai-module">
      <PageHeader
        title="Marketing AI"
        subtitle="Transforme tes inputs bruts en actifs marketing réutilisables (Claude Sonnet 4.6)"
        icon={Brain}
        breadcrumbs={[{ label: "Marketing", to: "/app/marketing-dashboard" }, { label: "Marketing AI" }]}
      />
      <div className="flex gap-1 mb-6 border-b border-zinc-800 overflow-x-auto">
        {TABS.map((t) => {
          const Ic = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${active ? "border-amber-500 text-amber-400" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}
            >
              <Ic size={16} weight={active ? "duotone" : "regular"} />{t.label}
            </button>
          );
        })}
      </div>
      {tab === "dashboard" && <DashboardTab onNew={() => setTab("generator")} />}
      {tab === "generator" && <GeneratorTab prefill={prefill} />}
      {tab === "contents" && <ContentsTab onReuse={reuse} />}
      {tab === "prompts" && <PromptsTab />}
      {tab === "winners" && <WinnersTab onReuse={reuse} />}
    </div>
  );
}
